/**
 * VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_FOUNDATION_V1
 * — Traffic Trend chart (task spec §12/§58/§59).
 *
 * No charting library exists in this repo (same audit conclusion as
 * `TrendLineChart`/`DonutGauge`) — a two-series line + light area for
 * <=90 daily points does not warrant adding one. Deliberately a
 * standalone component rather than extending the single-series
 * `TrendLineChart` (used elsewhere for Storage Trend) — a second series
 * changes the min/max/legend logic enough that reusing it would have
 * meant threading an optional-series prop through a component this
 * page does not otherwise touch.
 */
export interface MarketingTrendPoint {
  date: string
  visitors: number
  sessions: number
}

export default function MarketingTrendChart({
  points,
  visitorsLabel,
  sessionsLabel,
  emptyLabel,
  formatDate,
  height = 240,
}: {
  points: MarketingTrendPoint[]
  visitorsLabel: string
  sessionsLabel: string
  emptyLabel: string
  formatDate: (d: string) => string
  height?: number
}) {
  if (points.length === 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 13,
        }}
      >
        {emptyLabel}
      </div>
    )
  }

  const width = 680
  const padding = { top: 16, right: 16, bottom: 28, left: 8 }
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom

  const visitorColor = 'var(--pf-primary, #5B5FF5)'
  const sessionColor = 'var(--pf-info, #06B6D4)'

  const allValues = points.flatMap((p) => [p.visitors, p.sessions])
  const maxValue = Math.max(...allValues, 1)
  const minValue = 0
  const range = maxValue - minValue || 1

  const xStep = points.length > 1 ? innerWidth / (points.length - 1) : 0
  const coordsFor = (key: 'visitors' | 'sessions') =>
    points.map((p, i) => ({
      x: padding.left + i * xStep,
      y: padding.top + innerHeight - ((p[key] - minValue) / range) * innerHeight,
    }))

  const visitorCoords = coordsFor('visitors')
  const sessionCoords = coordsFor('sessions')

  const pathFor = (coords: { x: number; y: number }[]) =>
    coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')

  const visitorPath = pathFor(visitorCoords)
  const sessionPath = pathFor(sessionCoords)
  const visitorAreaPath = `${visitorPath} L${visitorCoords[visitorCoords.length - 1].x.toFixed(1)},${(padding.top + innerHeight).toFixed(1)} L${visitorCoords[0].x.toFixed(1)},${(padding.top + innerHeight).toFixed(1)} Z`

  const labelCount = Math.min(5, points.length)
  const labelIndices =
    labelCount <= 1 ? [0] : Array.from({ length: labelCount }, (_, i) => Math.round((i * (points.length - 1)) / (labelCount - 1)))

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 4, fontSize: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: visitorColor, display: 'inline-block' }} />
          {visitorsLabel}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: sessionColor, display: 'inline-block' }} />
          {sessionsLabel}
        </span>
      </div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={visitorsLabel}>
        <path d={visitorAreaPath} fill={visitorColor} opacity={0.08} />
        <path d={visitorPath} fill="none" stroke={visitorColor} strokeWidth={2} />
        <path d={sessionPath} fill="none" stroke={sessionColor} strokeWidth={2} strokeDasharray="4 3" />
        {visitorCoords.length <= 31 && visitorCoords.map((c, i) => <circle key={`v-${i}`} cx={c.x} cy={c.y} r={2.2} fill={visitorColor} />)}
        {sessionCoords.length <= 31 && sessionCoords.map((c, i) => <circle key={`s-${i}`} cx={c.x} cy={c.y} r={2.2} fill={sessionColor} />)}
        {labelIndices.map((idx) => (
          <text
            key={idx}
            x={visitorCoords[idx].x}
            y={height - 8}
            fontSize={11}
            fill="var(--text-muted)"
            textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'}
          >
            {formatDate(points[idx].date)}
          </text>
        ))}
        <title>
          {points.map((p) => `${formatDate(p.date)}: ${visitorsLabel} ${p.visitors} · ${sessionsLabel} ${p.sessions}`).join('\n')}
        </title>
      </svg>
    </div>
  )
}
